<template>
  <div class="allStoresPanel">
    <div class="allStoresPanel__header">
      <div class="allStoresPanel__title">
        {{ $t("app.home.map_store_query_title") }}
        <span v-if="totalCount" class="allStoresPanel__count"
          >({{ totalCount }})</span
        >
      </div>
      <button
        type="button"
        class="allStoresPanel__overviewBtn"
        @click="onOverview"
      >
        <van-icon name="replay" class="allStoresPanel__overviewBtn__icon" />
        <span>{{ $t("app.home.map_store_overview_button") }}</span>
      </button>
    </div>

    <div class="allStoresPanel__searchWrap">
      <van-field
        class="allStoresPanel__search"
        v-model.trim="innerKeyword"
        :clearable="false"
        :placeholder="$t('app.home.map_store_search_store_or_code_placeholder')"
      >
        <template #left-icon>
          <span class="allStoresPanel__searchIconWrap">
            <img class="allStoresPanel__searchIcon" :src="iconSearch" alt="" />
          </span>
        </template>
        <template #button>
          <van-icon
            v-show="innerKeyword && String(innerKeyword).length > 0"
            name="cross"
            class="allStoresPanel__searchClear"
            @click.stop="onClearKeyword"
          />
        </template>
      </van-field>
    </div>

    <div v-if="loading" class="allStoresPanel__loading">
      {{ $t("app.home.map_loading") }}
    </div>

    <div v-else-if="filteredGroups.length === 0" class="allStoresPanel__empty">
      <p class="allStoresPanel__emptyLine1">
        {{ $t("app.home.map_store_empty_area_line1") }}
      </p>
      <p class="allStoresPanel__emptyLine2">
        {{ $t("app.home.map_store_empty_area_line2") }}
      </p>
    </div>

    <div v-else class="allStoresPanel__list">
      <div
        v-for="(country, cIdx) in filteredGroups"
        :key="`${country.countryName}-${cIdx}`"
        class="allStoresPanel__country"
      >
        <div class="allStoresPanel__countryTitle">
          {{ country.countryName }}
        </div>

        <div
          v-for="(province, pIdx) in country.provinces"
          :key="`${country.countryName}-${province.provinceName}-${pIdx}`"
          class="allStoresPanel__province"
        >
          <div class="allStoresPanel__provinceTitle">
            <span>{{ province.provinceName }}</span>
            <span class="allStoresPanel__provinceCount">{{
              province.dealers.length
            }}</span>
          </div>

          <div
            v-for="item in province.dealers"
            :key="item.__listKey"
            class="store"
            :class="{ 'store--active': item.__listKey === activeKey }"
            @click="onSelect(item)"
          >
            <div class="store__head">
              <div class="store__name">{{ item.fullName || "-" }}</div>
            </div>
            <div class="store__main">
              <div class="store__rows">
                <div class="store__row" v-if="getDealerDisplayAddress(item)">
                  <img class="store__row__location" :src="iconLoc" alt="" />
                  <div class="store__row__text">
                    {{ getDealerDisplayAddress(item) }}
                  </div>
                  <button
                    type="button"
                    class="store__copyBtn"
                    @click.stop="onCopyAddress(item)"
                  >
                    {{ $t("app.home.map_popup_copy_address") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import iconLoc from "@/assets/map-icons/map_location.png";
import iconSearch from "@/assets/map-icons/map_search.png";
import { getDealerDisplayAddress } from "@/views/home/maptiler/useMapTilerAllStores";
import { useMapCountryGroupsFilter } from "@/views/home/maptiler/useMapCountryGroupsFilter";
import { useMapTilerCopyText } from "@/views/home/maptiler/useMapTilerCopyText";

export default {
  name: "MapTilerAllStoresPanel",
  props: {
    groups: { type: Array, default: () => [] },
    keyword: { type: String, default: "" },
    activeKey: { type: [String, Number], default: null },
    loading: { type: Boolean, default: false },
  },
  emits: ["update:keyword", "select", "overview"],
  
  setup(props, { emit }) {
    const { innerKeyword, filteredGroups, totalCount, onClearKeyword } =
      useMapCountryGroupsFilter(props, emit);
    const { copyDealerAddress } = useMapTilerCopyText();

    const onCopyAddress = (item) => {
      copyDealerAddress(item);
    };

    const onSelect = (item) => {
      emit("select", { item, key: item.__listKey });
    };

    const onOverview = () => {
      emit("overview");
    };

    return {
      innerKeyword,
      filteredGroups,
      totalCount,
      iconLoc,
      iconSearch,
      onClearKeyword,
      onSelect,
      onOverview,
      getDealerDisplayAddress,
      onCopyAddress,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/style/mixins.scss";
@import "@/style/viriables.scss";

.allStoresPanel {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 0.12rem;
  box-shadow: 0 0.06rem 0.24rem rgba(0, 0, 0, 0.12);
  overflow: hidden;

  &__header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.08rem;
    padding: 0.14rem 0.14rem 0.08rem;
  }

  &__title {
    flex: 1;
    min-width: 0;
    font-size: 0.15rem;
    font-weight: 700;
    color: $title-fontColor;
  }

  &__overviewBtn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.04rem;
    height: 0.28rem;
    padding: 0 0.1rem;
    border: 1px solid rgba(0, 147, 67, 0.35);
    border-radius: 999px;
    background: rgba(0, 147, 67, 0.08);
    color: $themeColor;
    font-size: 0.11rem;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    transition: opacity 0.15s ease, transform 0.15s ease;

    &__icon {
      font-size: 0.12rem;
    }

    &:hover {
      opacity: 0.9;
    }

    &:active {
      transform: scale(0.98);
    }
  }

  &__count {
    margin-left: 0.04rem;
    font-size: 0.12rem;
    font-weight: 500;
    color: #888;
  }

  &__searchWrap {
    flex-shrink: 0;
    padding: 0 0.12rem 0.08rem;
    box-sizing: border-box;
  }

  &__search {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #e8e8e8;
    border-radius: 999px;
    background: #fafafa;
    overflow: hidden;
  }

  &__searchIconWrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0.18rem;
    height: 0.18rem;
    flex-shrink: 0;
  }

  &__searchIcon {
    width: 0.16rem;
    height: 0.16rem;
    display: block;
    object-fit: contain;
  }

  &__searchClear {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 0.16rem;
    line-height: 1;
    cursor: pointer;
  }

  &__loading,
  &__empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.24rem;
    text-align: center;
    color: #888;
    font-size: 0.12rem;
  }

  &__emptyLine1 {
    margin: 0;
    font-size: 0.13rem;
    color: #666;
  }

  &__emptyLine2 {
    margin: 0.08rem 0 0;
    font-size: 0.12rem;
    color: #999;
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    padding: 0 0.12rem 0.12rem;
    -webkit-overflow-scrolling: touch;
  }

  &__countryTitle {
    margin: 0.12rem 0 0.08rem;
    padding-left: 0.04rem;
    font-size: 0.13rem;
    font-weight: 700;
    color: $themeColor;
    border-left: 0.03rem solid $themeColor;
  }

  &__province {
    margin-bottom: 0.14rem;
  }

  &__provinceTitle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.08rem;
    margin: 0.14rem 0 0.1rem;
    padding: 0.1rem 0.12rem;
    border-radius: 0.08rem;
    border-left: 0.04rem solid $themeColor;
    background: linear-gradient(90deg, rgba(0, 147, 67, 0.12) 0%, #f5f7f6 100%);
    font-size: 0.14rem;
    font-weight: 700;
    color: #2b2b2b;
    line-height: 1.2;
    box-shadow: 0 0.02rem 0.06rem rgba(0, 0, 0, 0.04);
  }

  &__provinceCount {
    flex-shrink: 0;
    min-width: 0.22rem;
    height: 0.22rem;
    padding: 0 0.07rem;
    border-radius: 0.11rem;
    background: $themeColor;
    color: #fff;
    font-size: 0.11rem;
    font-weight: 600;
    line-height: 0.22rem;
    text-align: center;
  }
}

.store {
  margin-bottom: 0.08rem;
  padding: 0.1rem;
  border: 1px solid #eee;
  border-radius: 0.1rem;
  background: #fff;
  cursor: pointer;

  &--active {
    border-color: $themeColor;
    box-shadow: 0 0 0 1px rgba(0, 147, 67, 0.12);
  }

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.08rem;
  }

  &__name {
    flex: 1;
    font-size: 0.13rem;
    font-weight: 600;
    color: #333;
    line-height: 1.3;
    @include ellipsis-two;
  }

  &__main {
    margin-top: 0.08rem;
  }

  &__rows {
    min-width: 0;
  }

  &__row {
    display: flex;
    gap: 0.06rem;
    align-items: flex-start;

    &__location {
      width: 0.14rem;
      height: 0.14rem;
      margin-top: 0.02rem;
      flex-shrink: 0;
    }

    &__text {
      flex: 1;
      font-size: 0.11rem;
      color: #666;
      line-height: 1.35;
      word-break: break-all;
    }
  }

  &__copyBtn {
    flex-shrink: 0;
    border: none;
    background: rgba(0, 147, 67, 0.08);
    color: $themeColor;
    font-size: 0.1rem;
    line-height: 1;
    padding: 0.05rem 0.08rem;
    border-radius: 0.04rem;
    cursor: pointer;

    &:hover {
      background: rgba(0, 147, 67, 0.14);
    }
  }
}

::v-deep(.allStoresPanel__search.van-cell) {
  padding: 0 0.1rem;
  background: transparent;
  overflow: hidden;
  align-items: center;
  min-height: 0.36rem;
  height: 0.36rem;
}

::v-deep(.allStoresPanel__search .van-cell__value) {
  overflow: hidden;
  display: flex;
  align-items: center;
}

::v-deep(.allStoresPanel__search .van-field__body) {
  align-items: center;
  min-width: 0;
  width: 100%;
  height: 0.36rem;
}

::v-deep(.allStoresPanel__search .van-field__left-icon) {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.06rem;
  height: 0.36rem;
  line-height: 1;
}

::v-deep(.allStoresPanel__search .van-field__control) {
  font-size: 0.12rem;
  line-height: 0.36rem;
  height: 0.36rem;
  padding: 0;
  min-width: 0;
}

::v-deep(.allStoresPanel__search .van-field__button) {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-left: 0.04rem;
  padding-right: 0;
  height: 0.36rem;
  line-height: 1;
}
</style>
